import type { ReactNode } from "react";
import {
  HiOutlineArrowUpTray,
  HiOutlineChatBubbleLeftRight,
  HiOutlineCheckBadge,
  HiOutlineClock,
  HiOutlineDocumentDuplicate,
  HiOutlineEnvelope,
  HiOutlineExclamationTriangle,
  HiOutlineNoSymbol,
  HiOutlineShieldCheck,
  HiOutlineXCircle,
} from "react-icons/hi2";

import {
  fileSourceView,
  scanStatusView,
  verificationView,
  type FileSourceKind,
} from "./fileLabels";

function Badge({
  tone,
  kind,
  icon,
  children,
}: {
  tone: string;
  kind: string;
  icon: ReactNode;
  children: ReactNode;
}) {
  // Смысл несёт текст; цвет и значок дублируют его, но не заменяют.
  return (
    <span className={`file-badge file-badge--${tone}`} data-kind={kind}>
      <span className="file-badge__icon" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </span>
  );
}

/** Состояние антивирусной проверки. */
export function ScanStatusBadge({ status }: { status: string }) {
  const view = scanStatusView(status);
  const icon =
    view.tone === "ok" ? (
      <HiOutlineShieldCheck />
    ) : view.tone === "wait" ? (
      <HiOutlineClock />
    ) : status === "quarantined" ? (
      <HiOutlineNoSymbol />
    ) : (
      <HiOutlineExclamationTriangle />
    );

  return (
    <Badge tone={view.tone} kind={`scan-${status}`} icon={icon}>
      {view.label}
    </Badge>
  );
}

const sourceIcons: Record<FileSourceKind, ReactNode> = {
  manual: <HiOutlineArrowUpTray />,
  email_attachment: <HiOutlineEnvelope />,
  comment_attachment: <HiOutlineChatBubbleLeftRight />,
  material: <HiOutlineDocumentDuplicate />,
  other: <HiOutlineDocumentDuplicate />,
};

/** Откуда файл взялся: ручная загрузка, письмо, обсуждение, материал. */
export function SourceBadge({ type }: { type: string }) {
  const view = fileSourceView(type);

  return (
    <Badge
      tone="source"
      kind={`source-${view.kind}`}
      icon={sourceIcons[view.kind]}
    >
      {view.label}
    </Badge>
  );
}

/** Есть ли у версии подпись. */
export function SignatureStateBadge({
  state,
}: {
  state: "signed" | "unsigned";
}) {
  return state === "signed" ? (
    <Badge tone="ok" kind="signature-signed" icon={<HiOutlineCheckBadge />}>
      Подписана
    </Badge>
  ) : (
    <Badge tone="neutral" kind="signature-unsigned" icon={<HiOutlineClock />}>
      Без подписи
    </Badge>
  );
}

/** Результат серверной проверки подписи. */
export function VerificationBadge({ state }: { state: string }) {
  const view = verificationView(state);

  return (
    <Badge
      tone={view.tone}
      kind={`verification-${state}`}
      icon={
        view.tone === "ok" ? (
          <HiOutlineShieldCheck />
        ) : view.tone === "blocked" ? (
          <HiOutlineXCircle />
        ) : (
          <HiOutlineClock />
        )
      }
    >
      {view.label}
    </Badge>
  );
}
